/**
 * CollapsibleChatPanel · v2.7.65+E106 (+E117 tiny-icon mode)
 * ══════════════════════════════════════════════════════════════════
 * Shared shell for public-facing (Katie) and admin (Ashley) chat
 * experiences. Two mounting variants:
 *
 *   variant="floating"  — pill launcher + bottom-right chat window,
 *                          used by Katie on every public page.
 *   variant="sidebar"   — hanging tab on the right edge that slides
 *                          a full-height side panel in, reserved for
 *                          future admin/Ashley layouts.
 *
 * v2.7.65+E117 additions:
 *   • Launcher pill offset to `bottom-20` so it never overlaps the
 *     Cookie Consent / Privacy Policy banner at `bottom-0`.
 *   • New "tiny icon" mode — user can dismiss the pill to a 36 × 36
 *     circle at the corner (persisted per browser). Freeing up the
 *     background layout so users can click underlying elements.
 *   • Panel gets a dedicated "minimize to icon" chevron alongside the
 *     existing close X so users have a fast one-tap collapse path.
 */
import { useEffect, useState } from "react";
import { MessageSquare, X, ChevronRight, ChevronLeft, ChevronDown } from "lucide-react";

const TINY_STORAGE_KEY = "ccp_launcher_tiny";

export default function CollapsibleChatPanel({
  title = "Chat",
  variant = "floating",
  triggerLabel,
  triggerSubLabel,
  triggerAvatar,          // optional React node rendered inside the launcher pill
  accent = "amber",       // "amber" | "sky" | "emerald" — pill colour
  statusDot = "bg-emerald-400 animate-pulse",  // v2.8+E304 · header status dot classes
  defaultOpen = false,
  onOpenChange,
  children,
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  // v2.7.65+E117 · Persist the compact "tiny icon" preference across visits.
  const [tinyMode, setTinyMode] = useState(() => {
    try { return window.localStorage.getItem(TINY_STORAGE_KEY) === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { window.localStorage.setItem(TINY_STORAGE_KEY, tinyMode ? "1" : "0"); }
    catch { /* quota */ }
  }, [tinyMode]);

  // v2.7.65+E118 · Cross-tab sync via BroadcastChannel — a rider with
  // Buller Taxi open in two tabs sees the tiny-mode + open state stay
  // consistent. Safari + Firefox + Chrome all support BroadcastChannel;
  // when unavailable we silently no-op.
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return undefined;
    let channel;
    try { channel = new BroadcastChannel("bt_katie_state"); }
    catch { return undefined; }
    channel.onmessage = (ev) => {
      const msg = ev.data || {};
      if (typeof msg.tinyMode === "boolean") setTinyMode(msg.tinyMode);
      if (typeof msg.isOpen === "boolean")   setIsOpen(msg.isOpen);
    };
    return () => { try { channel.close(); } catch { /* noop */ } };
  }, []);
  const broadcast = (patch) => {
    if (typeof BroadcastChannel === "undefined") return;
    try {
      const ch = new BroadcastChannel("bt_katie_state");
      ch.postMessage(patch);
      ch.close();
    } catch { /* noop */ }
  };

  const setOpen = (v) => {
    setIsOpen(v);
    onOpenChange?.(v);
    broadcast({ isOpen: v });
  };

  const setTiny = (v) => {
    setTinyMode(v);
    broadcast({ tinyMode: v });
  };

  const accentPillCls = {
    amber:   "bg-zinc-900 border-amber-400/40 hover:border-amber-400 shadow-[0_10px_30px_rgba(0,0,0,0.45)] hover:shadow-[0_16px_40px_rgba(251,191,36,0.25)]",
    sky:     "bg-zinc-900 border-sky-400/40   hover:border-sky-400   shadow-[0_10px_30px_rgba(0,0,0,0.45)] hover:shadow-[0_16px_40px_rgba(56,189,248,0.25)]",
    emerald: "bg-zinc-900 border-emerald-400/40 hover:border-emerald-400 shadow-[0_10px_30px_rgba(0,0,0,0.45)] hover:shadow-[0_16px_40px_rgba(52,211,153,0.25)]",
  }[accent] || "";

  const accentRingCls = {
    amber:   "border-amber-400/60 hover:border-amber-400",
    sky:     "border-sky-400/60   hover:border-sky-400",
    emerald: "border-emerald-400/60 hover:border-emerald-400",
  }[accent] || "";

  // v2.7.65+E117 · Panel repositioned above the cookie banner (which is
  // fixed at bottom-0 with ~48-64 px height) so opening the chat no
  // longer hides Accept / Refuse controls.
  const panelCls = variant === "floating"
    ? `fixed bottom-20 right-4 z-[70] w-[min(400px,calc(100vw-2rem))] h-[min(620px,calc(100vh-8rem))] rounded-2xl border border-zinc-800 shadow-[0_20px_60px_rgba(0,0,0,0.55)] bg-zinc-950 transition-all duration-300 flex flex-col overflow-hidden ${
        isOpen ? "translate-y-0 opacity-100 scale-100 pointer-events-auto"
               : "translate-y-4 opacity-0 scale-95 pointer-events-none"
      }`
    : `fixed top-0 right-0 z-[60] w-96 h-full border-l border-zinc-800 shadow-2xl bg-zinc-950 flex flex-col transition-transform duration-300 ${
        isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"
      }`;

  return (
    <>
      {/* Trigger */}
      {variant === "floating" ? (
        !isOpen && (
          tinyMode ? (
            /* v2.7.65+E117 · Tiny 36×36 corner icon that frees up the
               layout below (Privacy Policy / cookie banner / hero CTAs). */
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="ccp-launcher-tiny"
              title={`Open ${title}`}
              className={`fixed bottom-20 right-4 z-[70] h-9 w-9 rounded-full border ${accentRingCls} bg-zinc-900 flex items-center justify-center shadow-lg hover:scale-110 transition-transform`}
              aria-label={`Open ${title}`}
            >
              <MessageSquare className="h-4 w-4 text-white"/>
            </button>
          ) : (
            <div className="fixed bottom-20 right-4 z-[70] flex items-center gap-1.5" data-testid="ccp-launcher-wrapper">
              <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="ccp-launcher"
                className={`group flex items-center gap-2 rounded-full pl-1 pr-4 py-1 border transition-all ${accentPillCls}`}
                aria-label={`Open ${title}`}
              >
                {triggerAvatar}
                <div className="text-left leading-tight">
                  <div className="text-xs font-bold uppercase tracking-widest text-white">
                    {triggerLabel || title}
                  </div>
                  {triggerSubLabel && (
                    <div className="text-[10px] text-zinc-400">{triggerSubLabel}</div>
                  )}
                </div>
                <MessageSquare className="h-4 w-4 text-white/70 opacity-0 group-hover:opacity-100 transition-opacity ml-1"/>
              </button>
              {/* Dismiss X — shrinks the pill to the tiny icon */}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setTiny(true); }}
                data-testid="ccp-launcher-shrink"
                title="Shrink to icon · free up the page"
                className="h-6 w-6 rounded-full bg-zinc-900/80 border border-zinc-700 flex items-center justify-center text-zinc-400 hover:text-white hover:border-zinc-500"
                aria-label="Shrink launcher"
              >
                <X className="h-3 w-3"/>
              </button>
            </div>
          )
        )
      ) : (
        <button
          type="button"
          onClick={() => setOpen(!isOpen)}
          data-testid="ccp-sidebar-toggle"
          className={`fixed top-1/2 -translate-y-1/2 z-[55] bg-zinc-900 border border-zinc-700 text-zinc-200 p-2 rounded-l-md shadow-md transition-all ${
            isOpen ? "right-96" : "right-0"
          }`}
          aria-label={isOpen ? "Close" : "Open"}
        >
          {isOpen ? <ChevronRight className="h-5 w-5"/> : <ChevronLeft className="h-5 w-5"/>}
        </button>
      )}

      {/* Panel */}
      <div className={panelCls} data-testid="ccp-panel" role="dialog" aria-label={title}>
        {/* Common header + close */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-zinc-900 via-zinc-900 to-zinc-900/70">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full ${statusDot}`}/>
            <span className="font-bold text-sm text-zinc-100">{title}</span>
          </div>
          <div className="flex items-center gap-1">
            {/* v2.7.65+E117 · Minimize-to-icon chevron. Closes the
                panel AND flips the launcher into tiny mode so a user
                who wanted to click the Privacy Policy button reclaims
                the corner in a single tap. */}
            {variant === "floating" && (
              <button
                type="button"
                onClick={() => { setTiny(true); setOpen(false); }}
                data-testid="ccp-minimize"
                className="text-zinc-500 hover:text-amber-300 p-1"
                aria-label="Minimize to icon"
                title="Minimize to icon"
              >
                <ChevronDown className="h-4 w-4"/>
              </button>
            )}
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="ccp-close"
              className="text-zinc-500 hover:text-zinc-100 p-1"
              aria-label={`Close ${title}`}
            >
              <X className="h-4 w-4"/>
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-hidden">
          {children}
        </div>
      </div>
    </>
  );
}
